import { useEffect, useState } from "react";
import { TextArea } from "components/ui/TextArea";
import { Button } from "components/ui/Button";
import { SubscriptionPlans } from "components/SubscriptionPlans";
import fetchWithCredentials from "apiClient";
import { useAuth } from "context/AuthContext";
import { useStudents } from "context/StudentsContext";
import { X, AlertTriangle } from "lucide-react";

export default function Settings() {
  const [instructions, setInstructions] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [, setCurrentPlan] = useState("Free");
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const { hasPartner, planLimit, partnerName, currentPlan } = useAuth();
  const { activeStudentsCount } = useStudents();

  useEffect(() => {
    async function fetchSettings() {
      try {
        const data = await fetchWithCredentials(`/api/teacher/settings/`);
        setInstructions(data.instructions || "");
        setCurrentPlan(data.plan || "Free");
      } catch (err) {
        console.error("Error loading teacher settings:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchSettings();
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    setSuccess(false);
    try {
      await fetchWithCredentials(`/api/teacher/settings/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ instructions }),
      });
      setSuccess(true);
    } catch (err) {
      console.error("Error saving teacher settings:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await fetchWithCredentials(`/api/teacher/cancel-subscription/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });
      setShowCancelDialog(false);
      // Refresh the page to update the plan status
      window.location.reload();
    } catch (err) {
      console.error("Error cancelling subscription:", err);
    } finally {
      setCancelling(false);
    }
  };

  const showUpgradeBanner = activeStudentsCount >= planLimit;

  return (
    <div className="w-full h-full overflow-y-auto px-4 lg:px-6 py-4 space-y-6 lg:space-y-8 max-w-7xl bg-white">
      <h1 className="text-2xl font-bold text-gray-900 mb-6 ml-16 lg:ml-0">
        Settings
      </h1>
      {loading ? (
        <p className="text-gray-500 text-xs lg:text-sm">Loading settings...</p>
      ) : (
        <>
          {showUpgradeBanner && (
            <div className="p-3 lg:p-4 rounded-lg bg-yellow-50 border border-yellow-200">
              <p className="text-xs lg:text-sm text-yellow-700">
                {hasPartner
                  ? `You've reached your student limit. Contact ${partnerName} to upgrade your plan.`
                  : `You've reached your student limit. Select a plan below to add more students.`}
              </p>
            </div>
          )}

          {!hasPartner && (
            <div className="space-y-3 lg:space-y-4">
              <h2 className="text-lg lg:text-xl font-semibold">
                Subscription Plan
              </h2>
              <SubscriptionPlans
                onCancelSubscription={() => setShowCancelDialog(true)}
                showCancelButton={currentPlan !== "Free"}
              />
            </div>
          )}

          <div className="space-y-3 lg:space-y-4">
            <h2 className="text-lg lg:text-xl font-semibold">Instructions</h2>
            <TextArea
              label="Instructions"
              value={instructions}
              onChange={setInstructions}
              placeholder="Instructions for Eduvia"
              rows={8}
            />

            <div className="flex items-center gap-3 lg:gap-4">
              <Button
                onClick={saveSettings}
                disabled={saving}
                variant="primary"
                size="sm"
              >
                {saving ? "Saving..." : "Save"}
              </Button>

              {success && (
                <span className="text-xs lg:text-sm text-green-600">
                  Saved successfully
                </span>
              )}
            </div>
          </div>
        </>
      )}

      {/* Cancel Subscription Confirmation Dialog */}
      {showCancelDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Cancel Subscription
              </h3>
              <button
                onClick={() => setShowCancelDialog(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6">
              <div className="flex items-start space-x-3 mb-4">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-700">
                    Are you sure you want to cancel your subscription?
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Your subscription will remain active until the next billing
                    cycle. You can resubscribe at any time.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <Button
                onClick={() => setShowCancelDialog(false)}
                variant="secondary"
                size="sm"
                className="flex-1"
                disabled={cancelling}
              >
                Keep Subscription
              </Button>
              <Button
                onClick={handleCancelSubscription}
                variant="secondary"
                size="sm"
                className="flex-1 bg-red-600 hover:bg-red-700 text-white border-red-600"
                disabled={cancelling}
              >
                {cancelling ? "Cancelling..." : "Cancel Subscription"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
